import { IChecklist, IChecklistItem, IChecklistSummaryRow } from "@mctypes/checklist.types";
import { Flex } from "@mantine/core";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo } from "react";
import { HSpace } from "../../../components";
import { SingleStamp } from "./admin/StampConfiguration";
import { summarizeChecklistItems } from "./checklistSummary";

const HeaderWithStamp: React.FC<{
  label: string;
  config?: { text: string; color: string; icon: string };
}> = ({ label, config }) => {
  return (
    <Flex style={{ alignItems: "center" }}>
      {config && <SingleStamp config={config} />}
      <HSpace />
      <span>{label}</span>
    </Flex>
  );
};

export const ChecklistSummaryTable: React.FC<{
  items: IChecklistItem[];
  checklist: IChecklist;
}> = ({ items, checklist }) => {
  const rows = useMemo(() => summarizeChecklistItems(items, checklist), [items, checklist]);

  // Fallback om IR saknas i stampSettings
  const irStamp = checklist.stampSettings.ir ?? ({ text: "IR", color: "#9aa0a6", icon: "minus" } as const);

  const columnDefs = useMemo<ColDef<IChecklistSummaryRow>[]>(
    () => [
      {
        headerName: "Sjekk",
        field: "checkTitle",
        width: 100,
        sortable: false,
        filter: false,
        suppressMenu: true,
        headerClass: "cell-flex cell-align-center",
        valueGetter: (p) => (p.node?.rowPinned ? "Sum" : p.data?.checkTitle),
      },
      {
        field: "accepted",
        width: 130,
        sortable: false,
        filter: false,
        suppressMenu: true,
        headerComponent: HeaderWithStamp,
        headerComponentParams: {
          label: checklist.stampSettings.yes.text,
          config: checklist.stampSettings.yes,
        },
      },
      {
        field: "rejected",
        width: 150,
        sortable: false,
        filter: false,
        suppressMenu: true,
        headerComponent: HeaderWithStamp,
        headerComponentParams: {
          label: checklist.stampSettings.no.text,
          config: checklist.stampSettings.no,
        },
      },
      {
        field: "notRelevant",
        width: 145,
        sortable: false,
        filter: false,
        suppressMenu: true,
        headerComponent: HeaderWithStamp,
        headerComponentParams: {
          label: checklist.stampSettings.ir.text,
          config: checklist.stampSettings.ir,
        },
      },
      {
        field: "notActive",
        width: 160,
        sortable: false,
        filter: false,
        suppressMenu: true,
        headerComponent: HeaderWithStamp,
        headerComponentParams: {
          label: checklist.stampSettings.ir.text,
          config: checklist.stampSettings.ir,
        },
      },
      {
        field: "notDone",
        width: 130,
        sortable: false,
        filter: false,
        suppressMenu: true,
        headerComponent: HeaderWithStamp,
        headerComponentParams: {
          label: checklist.stampSettings.uav.text,
          config: checklist.stampSettings.uav,
        },
      },
      {
        field: "notFilled",
        width: 130,
        sortable: false,
        filter: false,
        suppressMenu: true,
        headerComponent: HeaderWithStamp,
        headerComponentParams: {
          label: checklist.stampSettings.na.text ?? "-",
          config: checklist.stampSettings.na,
        },
      },
      {
        headerName: "Status",
        field: "percentText",
        sortable: false,
        filter: false,
        suppressMenu: true,
        cellRenderer: ReadyCell,
      },
    ],
    [checklist.stampSettings, irStamp],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      filter: false,
      resizable: true,
      suppressHeaderMenuButton: true,
    }),
    [],
  );

  return (
    <div
      className="ag-theme-quartz"
      style={{ width: "100%", maxHeight: "56vh", overflowY: "auto" }} // dynamisk höjd
    >
      <AgGridReact<IChecklistSummaryRow>
        rowData={rows}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        domLayout="autoHeight"
        suppressNoRowsOverlay={false}
      />
    </div>
  );
};

const ReadyCell: React.FC<ICellRendererParams<IChecklistSummaryRow>> = ({ data }) => {
  if (!data) return null;
  const isReady = data.rejected === 0 && data.notFilled === 0 && data.totalEligible > 0;

  const pillStyle: React.CSSProperties = {
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    color: "#fff",
    background: isReady ? "#2e7d32" /* grön */ : "#c62828" /* röd */,
    lineHeight: 1.4,
    whiteSpace: "nowrap",
  };

  return (
    <Flex align="center" justify="flex-end" className="gap-2">
      <strong style={{ fontVariantNumeric: "tabular-nums", marginRight: "0.4em" }}>{data.percentText}</strong>
      <span style={pillStyle}>{isReady ? "Klar" : "Ikke klar"}</span>
    </Flex>
  );
};
